/**
 * generateICS — Calendar export utility (.ics format)
 * 
 * Generates standard iCalendar files for training sessions.
 * Compatible with Apple Calendar, Google Calendar, Outlook.
 */

export interface CalendarEvent {
  uid: string;
  title: string;
  description: string;
  start: Date;
  end: Date;
  url?: string;
  location?: string;
  /** If true, generate an all-day event (VALUE=DATE) instead of timed */
  allDay?: boolean;
}

const PRODID = "-//Perform//Training Calendar//FR";
const BASE_URL = typeof window !== "undefined" ? window.location.origin : "";

/** Escape special characters for ICS text fields */
function escapeICS(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/;/g, "\\;")
    .replace(/,/g, "\\,")
    .replace(/\n/g, "\\n");
}

/** Format Date to ICS datetime (YYYYMMDDTHHMMSS) */
function formatICSDate(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}T` +
    `${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`
  );
}

/** Generate ICS file content from a list of events */
export function generateICS(events: CalendarEvent[]): string {
  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    `PRODID:${PRODID}`,
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "X-WR-CALNAME:Perform – Entraînement",
  ];

  for (const event of events) {
    const dtstamp = `DTSTAMP:${formatICSDate(new Date())}`;
    lines.push(
      "BEGIN:VEVENT",
      `UID:${event.uid}`,
      dtstamp,
      `DTSTART:${formatICSDate(event.start)}`,
      `DTEND:${formatICSDate(event.end)}`,
      `SUMMARY:${escapeICS(event.title)}`,
      `DESCRIPTION:${escapeICS(event.description)}`,
    );
    if (event.url) {
      lines.push(`URL:${event.url}`);
    }
    if (event.location) {
      lines.push(`LOCATION:${escapeICS(event.location)}`);
    }
    lines.push("END:VEVENT");
  }

  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
}

/** Trigger browser download of an ICS file (legacy, kept for compatibility) */
export function downloadICS(content: string, filename: string): void {
  const blob = new Blob([content], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".ics") ? filename : `${filename}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Format Date to Google Calendar datetime string (UTC) */
function toGoogleDate(date: Date): string {
  return date.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
}

/**
 * Open calendar directly without visible file download.
 * - Android → Google Calendar event creation URL
 * - iOS / Desktop → inline ICS via data URI (triggers native calendar)
 *
 * For multiple events, always uses inline ICS (Google Cal URL only supports 1 event).
 */
export function openCalendarDirect(events: CalendarEvent[]): void {
  if (events.length === 0) return;

  const isAndroid = /Android/i.test(navigator.userAgent);
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Android with single event → Google Calendar URL
  if (isAndroid && events.length === 1) {
    const ev = events[0];
    const googleUrl =
      "https://calendar.google.com/calendar/render?action=TEMPLATE" +
      "&text=" + encodeURIComponent(ev.title) +
      "&dates=" + toGoogleDate(ev.start) + "/" + toGoogleDate(ev.end) +
      "&details=" + encodeURIComponent(ev.description + (ev.url ? "\n\n" + ev.url : ""));
    window.open(googleUrl, "_blank");
    return;
  }

  // Android with multiple events → open each in Google Calendar
  if (isAndroid && events.length <= 5) {
    for (const ev of events) {
      const googleUrl =
        "https://calendar.google.com/calendar/render?action=TEMPLATE" +
        "&text=" + encodeURIComponent(ev.title) +
        "&dates=" + toGoogleDate(ev.start) + "/" + toGoogleDate(ev.end) +
        "&details=" + encodeURIComponent(ev.description + (ev.url ? "\n\n" + ev.url : ""));
      window.open(googleUrl, "_blank");
    }
    return;
  }

  // iOS / Desktop / Android with many events → inline ICS via data URI
  const icsContent = generateICS(events);

  if (isIOS) {
    // data URI triggers Safari to propose "Add to Calendar"
    const dataUri = "data:text/calendar;charset=utf-8," + encodeURIComponent(icsContent);
    window.location.href = dataUri;
    return;
  }

  // Desktop & Android fallback: blob URL opened inline
  const blob = new Blob([icsContent], { type: "text/calendar;charset=utf-8" });
  const blobUrl = URL.createObjectURL(blob);
  window.open(blobUrl, "_self");
  // Clean up after a delay to let the OS process the file
  setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
}

/** Day key to JS day offset from Monday (0-indexed) */
const DAY_OFFSETS: Record<string, number> = {
  mon: 0, tue: 1, wed: 2, thu: 3, fri: 4, sat: 5, sun: 6,
};

/** French day name to day key */
const FRENCH_TO_KEY: Record<string, string> = {
  lundi: "mon", mardi: "tue", mercredi: "wed", jeudi: "thu",
  vendredi: "fri", samedi: "sat", dimanche: "sun",
};

/** Get current week's Monday as a Date (local time, midnight) */
export function getCurrentMonday(): Date {
  const now = new Date();
  const day = now.getDay(); // 0=Sun, 1=Mon, ...
  const diff = day === 0 ? -6 : 1 - day;
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + diff);
  return monday;
}

/** Resolve a day name (French or key) + week offset into a Date */
export function resolveDayDate(
  dayNameOrKey: string,
  weekOffset: number,
  baseMonday: Date
): Date {
  const key = FRENCH_TO_KEY[dayNameOrKey.toLowerCase().trim()] ?? dayNameOrKey.toLowerCase().trim();
  const offset = DAY_OFFSETS[key] ?? 0;
  const date = new Date(baseMonday);
  date.setDate(date.getDate() + weekOffset * 7 + offset);
  return date;
}

/** Build a Date from a base date + time string "HH:MM" */
export function setTimeOnDate(date: Date, time: string): Date {
  const [h, m] = time.split(":").map(Number);
  const result = new Date(date);
  result.setHours(h || 9, m || 0, 0, 0);
  return result;
}

/** Build session deep link */
export function sessionUrl(sessionId: string): string {
  return `${BASE_URL}/sessions?session=${sessionId}`;
}

/**
 * Build ICS event description for a training session.
 * Includes objective, top exercises, and deep link with emoji.
 */
/** Detect session type and return a contextual emoji */
export function getSessionEmoji(title: string, objective?: string): string {
  const text = `${title} ${objective ?? ""}`.toLowerCase();
  if (/sprint|speed|accéléra|accelera|vitesse/.test(text)) return "⚡";
  if (/strength|gym|force|muscul|renforc/.test(text)) return "💪";
  if (/endurance|run|course|aérobi|aerobi|cardio|footing/.test(text)) return "🏃";
  if (/récup|recup|recovery|mobilit|étirement|etirement|stretch|breath|respir|souplesse/.test(text)) return "🧘";
  if (/injury|blessure|rehab|réhab|rééducat|reeducat|physio|kiné|kine/.test(text)) return "🩹";
  if (/prévent|prevent|prehab|préhab|stabilit/.test(text)) return "🛡";
  if (/nutrition|hydrat|diet|aliment/.test(text)) return "🍎";
  return "🏋️";
}

/** Keywords that indicate a recovery / breathing / mobility / nutrition session */
const RECOVERY_KEYWORDS = /r[ée]cup|respir|mobilit[ée]|[ée]tirement|stretching|breathing|recovery|nutrition|sommeil|sleep/i;

export function buildSessionDescription(opts: {
  title?: string;
  objective?: string;
  exercises?: { name: string }[];
  sessionId?: string;
  durationMinutes?: number;
}): string {
  const parts: string[] = [];

  if (opts.title) {
    parts.push(opts.title);
  }

  parts.push(`Objectif : ${opts.objective || "Séance d'entraînement"}`);

  if (opts.durationMinutes) {
    parts.push(`Durée : ${opts.durationMinutes} min`);
  }

  // Build key reminder from exercises or recovery hint
  const allText = [opts.title, opts.objective, ...(opts.exercises?.map((e) => e.name) ?? [])].join(" ");
  const isRecovery = RECOVERY_KEYWORDS.test(allText);

  const keyPoints = opts.exercises?.slice(0, 2).map((e) => e.name).join(", ");

  parts.push("");
  parts.push("Rappel clé :");
  if (isRecovery) {
    parts.push("Respiration contrôlée recommandée.");
    parts.push("Réduire l'intensité en cas d'inconfort.");
  }
  if (keyPoints) {
    parts.push(keyPoints);
  }
  if (!isRecovery && !keyPoints) {
    parts.push("Voir la séance complète dans l'app.");
  }

  if (opts.sessionId) {
    parts.push("");
    parts.push("📲 Ouvrir la séance :");
    parts.push(sessionUrl(opts.sessionId));
  }

  return parts.join("\n");
}
